import { type RefObject, useEffect, useState } from "react"

/**
 * Mobile-only auto-hide signal for the explorer search band.
 *
 * The search only starts hiding once the toolbar is actually **pinned** under
 * the navbar — detected via `pinRef` (the sticky rail) reaching its stuck top.
 * While the toolbar is still scrolling up through the hero the search stays
 * fully visible, so it never vanishes mid-screen. Once pinned, scrolling down
 * hides it and scrolling up reveals it, with an 8px hysteresis so momentum
 * jitter doesn't flip it back and forth.
 *
 * No-op (always `false`) at >= md — desktop keeps the search pinned.
 */
export function useHideOnScrollDown(
  pinRef: RefObject<HTMLElement | null>
): boolean {
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    if (!mq.matches) return

    let lastY = window.scrollY
    let acc = 0
    let raf = 0
    // Asymmetric hysteresis: hide quickly on a small downward scroll, but
    // require a longer, deliberate upward scroll to reveal — so the search
    // eases back in calmly instead of popping in on the first upward pixel.
    const HIDE_THRESHOLD = 8
    const REVEAL_THRESHOLD = 44

    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const y = window.scrollY
        const dy = y - lastY
        lastY = y
        if (dy === 0) return

        // Pinned once the rail has reached its sticky top under the navbar
        // (shown ≈ 130px, hidden ≈ 64px after the follow-up transform — both
        // well under the hero, where the rail sits far lower).
        const el = pinRef.current
        const pinned = el ? el.getBoundingClientRect().top <= 132 : false
        if (!pinned) {
          acc = 0
          setHidden(false)
          return
        }

        // Accumulate scroll in the current direction; reset on a flip. Only
        // cross the hysteresis threshold flips the state, killing jitter.
        if (dy > 0 !== acc > 0) acc = 0
        acc += dy
        if (acc > HIDE_THRESHOLD) setHidden(true)
        else if (acc < -REVEAL_THRESHOLD) setHidden(false)
      })
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [pinRef])

  return hidden
}
